/**
 * THE COMPANY AND THE BRANCHES, AND THE ONLY PLACE THAT WIRE IS KNOWN.
 *
 * PharmOS API 1.0.0, SwaggerHub `Last-Modified` 02-10-2026 02:39 UTC.
 *
 * `GET /v1/company` reads the company. Nothing in the document writes it.
 * `GET /v1/branches` lists every branch as `BranchAndRolesObj`: the code, the
 * two names, `BranchEditable`, and the role names. There is no operation that
 * opens a branch, and none that closes or reopens one.
 * `PUT /v1/branches/{branchCode}` writes `BranchEditable` — the two addresses,
 * phone, hours, the licence numbers, the pharmacist — and answers
 * `BaseResponse`. The address split into `addressEn` / `addressZh` in the
 * 29-09-2026 10:24 UTC republish. The same nine fields come back on the list
 * when the server sends them. The branches table draws the address in the
 * reader's language, with the phone under it; the dialogue seeds its nine
 * boxes from the row and omits a cleared box from the body
 * (docs/DECISIONS.md, docs/features/settings/GAPS.md B9).
 *
 * `BranchRefObj.programmes` is the modules a branch subscribes to. It is
 * parsed here and not drawn: enrolment is out of the MVP.
 *
 * The logo is the branch's file, from the 02-10-2026 02:39 UTC republish.
 * `GET /v1/branches/{branchCode}/logo` returns `application/octet-stream`.
 * `POST` sends one multipart part named `file` and answers `BaseResponse`.
 * `DELETE` answers `BaseResponse`. The description says a `.png` of at most
 * `1M` and names neither a status nor a byte count, so nothing here refuses
 * a file. An empty download stays an empty blob: the document does not say
 * what a missing logo answers. The branch dialogue writes the file and the
 * name-menu circle reads the signed-in branch's (`branch-form.tsx`,
 * `routes/_authenticated.tsx`; `DECISIONS.md` 02-10-2026).
 */
import { z } from "zod";
import { branchProgrammesSchema } from "@/api/branch-programme";
import { createApiClient } from "@/api/client";
import type { paths } from "@/api/generated/pharmos-api";
import { AppError } from "@/api/problems";
import { acceptBaseResponse, payloadOf } from "./envelope";

const BRANCH = "/v1/branches/{branchCode}" as const;
const LOGO = "/v1/branches/{branchCode}/logo" as const;

function client() {
  return createApiClient<paths>();
}

const companySchema = z.object({
  companyNameEn: z.string().optional(),
  companyNameZh: z.string().optional(),
  contractStartDate: z.string().optional(),
  contractEndDate: z.string().optional(),
});

const roleRefSchema = z.object({
  roleCode: z.string().optional(),
  roleNameEn: z.string().optional(),
  roleNameZh: z.string().optional(),
});

/** `BranchEditable`, composed onto `BranchAndRolesObj`; the address split into
 *  two languages in the 29-09-2026 10:24 UTC republish. */
const branchSchema = z.object({
  branchCode: z.string().optional(),
  branchNameEn: z.string().optional(),
  branchNameZh: z.string().optional(),
  programmes: branchProgrammesSchema,
  roles: z.array(roleRefSchema).optional(),
  addressEn: z.string().optional(),
  addressZh: z.string().optional(),
  phone: z.string().optional(),
  openingHoursEn: z.string().optional(),
  openingHoursZh: z.string().optional(),
  hcpNumber: z.string().optional(),
  hslNumber: z.string().optional(),
  aspNumber: z.string().optional(),
  pharmacist: z.string().optional(),
});

const branchListSchema = z.object({
  /** Required here: an absent branch list cannot be shown as empty (GAPS.md B6). */
  branches: z.array(branchSchema),
});

export type CompanyRecord = z.infer<typeof companySchema>;
export type BranchRecord = z.infer<typeof branchSchema>;
export type BranchRoleRef = z.infer<typeof roleRefSchema>;

/** The write body. The trading name is not on it. */
export type BranchWrite = paths[typeof BRANCH]["put"]["requestBody"]["content"]["application/json"];

export async function readCompany(): Promise<CompanyRecord> {
  const path = "/v1/company";
  const { data, response } = await client().GET(path, {});
  return payloadOf(companySchema, data, path, response.status);
}

/** Every branch, in the order the server sent. The request carries no page and no sort. */
export async function listBranches(): Promise<readonly BranchRecord[]> {
  const path = "/v1/branches";
  const { data, response } = await client().GET(path, {});
  return payloadOf(branchListSchema, data, path, response.status).branches;
}

/** Writes the editable facts. The answer is the envelope, not the branch. */
export async function updateBranch(branchCode: string, body: BranchWrite): Promise<void> {
  const { data, response } = await client().PUT(BRANCH, { params: { path: { branchCode } }, body });
  acceptBaseResponse(data, BRANCH, response.status);
}

/** The branch's logo file. An empty body is an empty blob, not a missing record. */
export async function readBranchLogo(branchCode: string): Promise<Blob> {
  const { data, response } = await client().GET(LOGO, {
    params: { path: { branchCode } },
    headers: { Accept: "application/octet-stream" },
    parseAs: "blob",
  });
  if (!(data instanceof Blob)) {
    throw new AppError("contract", {
      status: response.status,
      message: `${LOGO} did not return application/octet-stream`,
    });
  }
  return data;
}

/**
 * Uploads the logo. The generated body types `file` as a string. The wire is
 * one multipart part of that name, so the serializer sends the file and the
 * typed body is not what leaves.
 */
export async function uploadBranchLogo(branchCode: string, file: Blob): Promise<void> {
  const form = new FormData();
  form.set("file", file);
  const { data, response } = await client().POST(LOGO, {
    params: { path: { branchCode } },
    body: {},
    bodySerializer: () => form,
  });
  acceptBaseResponse(data, LOGO, response.status);
}

/** Deletes the logo. The answer is the envelope, not the file. */
export async function deleteBranchLogo(branchCode: string): Promise<void> {
  const { data, response } = await client().DELETE(LOGO, { params: { path: { branchCode } } });
  acceptBaseResponse(data, LOGO, response.status);
}
